import React, { useState, useEffect } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Divider,
  InputBase,
  Button,
  Typography,
  makeStyles,
  CircularProgress,
  Backdrop,
  Box,
  IconButton,
  Tooltip,
} from "@material-ui/core";

import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import CancelIcon from "@material-ui/icons/Cancel";
import EventNoteIcon from "@material-ui/icons/EventNote";
import PersonIcon from "@material-ui/icons/Person";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import VisibilityIcon from "@material-ui/icons/Visibility";

import {
  Releasing,
  ReleaseSave,
  ReleaseCancel,
  findAuctionLot,
} from "../../../adminModel/auctions";

import AddReleasingDialog from "./AddReleasingDialog";

import { useAlert } from "react-alert";

import Api from "../../../Api";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },

  card: {
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #e5e7eb",
    backgroundColor: "#fff",
  },

  addCard: {
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #e5e7eb",
    backgroundColor: "#fff",
    marginBottom: theme.spacing(3),
  },

  sectionHeader: {
    padding: theme.spacing(2, 2.5),
    backgroundColor: "#fafafa",
  },

  sectionTitle: {
    fontWeight: 600,
  },

  sectionSubtitle: {
    color: theme.palette.text.secondary,
    marginTop: 3,
  },

  addContent: {
    padding: theme.spacing(2.5),
  },

  searchSection: {
    padding: theme.spacing(2),
    backgroundColor: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },

  searchGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
    maxWidth: 520,
  },

  searchBox: {
    display: "flex",
    alignItems: "center",
    width: 340,
    maxWidth: "100%",
    height: 44,
    padding: "0 12px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    backgroundColor: "#fff",

    "&:focus-within": {
      borderColor: theme.palette.primary.main,
    },
  },

  searchInput: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
  },

  clearButton: {
    padding: 4,
  },

  searchButton: {
    height: 44,
    minWidth: 100,
    fontWeight: 600,
    textTransform: "none",
  },

  tableContainer: {
    overflowX: "auto",
  },

  table: {
    minWidth: 950,
  },

  tableHead: {
    backgroundColor: "#f8fafc",
  },

  tableHeader: {
    fontWeight: 600,
    color: "#374151",
    whiteSpace: "nowrap",
    padding: "14px 16px",
  },

  tableCell: {
    padding: "14px 16px",
  },

  tableRow: {
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
  },

  status: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: "#f3f4f6",
  },

  actionWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },

  actionButton: {
    width: 38,
    height: 38,
    padding: 0,
  },

  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: "#9ca3af",
  },

  pagination: {
    borderTop: "1px solid #e5e7eb",
  },

  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
}));

const PaginatedTableV4 = () => {
  const classes = useStyles();
  const alert = useAlert();

  // =========================
  // TABLE
  // =========================
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // =========================
  // LOADING
  // =========================
  const [loading, setLoading] = useState(false);

  // =========================
  // SEARCH
  // =========================
  const [searchTerm, setSearchTerm] = useState("");

  // =========================
  // GET RELEASING
  // =========================
  const getReleasing = async (
    pageIndex,
    limit,
    search = ""
  ) => {
    try {
      const results = await Releasing(
        pageIndex,
        limit,
        search
      );

      if (
        !results ||
        !Array.isArray(results.data)
      ) {
        setData([]);
        setTotalCount(0);
        return;
      }

      setData(results.data);

      const total = parseInt(
        results.totalItems,
        10
      );

      setTotalCount(
        isNaN(total) ? 0 : total
      );
    } catch (error) {
      console.error(
        "Failed to fetch releasing data:",
        error
      );

      setData([]);
      setTotalCount(0);

      alert.error(
        "Failed to load releasing records."
      );
    }
  };

  // =========================
  // INITIAL LOAD
  // =========================
  useEffect(() => {
    getReleasing(
      page + 1,
      rowsPerPage,
      searchTerm
    );
  }, [page, rowsPerPage]);

  // =========================
  // SEARCH
  // =========================
  const handleSearch = () => {
    setPage(0);

    getReleasing(
      1,
      rowsPerPage,
      searchTerm
    );
  };

  // =========================
  // CLEAR SEARCH
  // =========================
  const clearSearch = () => {
    setSearchTerm("");
    setPage(0);

    getReleasing(
      1,
      rowsPerPage,
      ""
    );
  };

  // =========================
  // PAGINATION
  // =========================
  const handleChangePage = (
    event,
    newPage
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event
  ) => {
    setRowsPerPage(
      parseInt(
        event.target.value,
        10
      )
    );

    setPage(0);
  };

  // =========================
  // DATE FORMAT
  // =========================
  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  };

  // =========================
  // ESCAPE HTML
  // =========================
  const escapeHtml = (value) => {
    if (
      value === null ||
      value === undefined
    ) {
      return "";
    }

    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  // =========================
  // ADD RELEASING
  // =========================
  const handleAddSubmit = async (
    formData
  ) => {
    setLoading(true);

    const Dataform = {
      auction_id: formData.auction_id,
      lot_number: formData.lot_number,
      client_name: formData.client_name,
      deliver: formData.deliver,
    };

    try {
      const result = await ReleaseSave(
        Dataform
      );

      if (!result || !result.sucess) {
        alert.error(
          (result && result.error) ||
            "Failed to save releasing"
        );

        return;
      }

      alert.success(
        result.msg ||
          "Releasing successfully saved."
      );

      await getReleasing(
        page + 1,
        rowsPerPage,
        searchTerm
      );
    } catch (error) {
      console.error(
        "Failed to save releasing:",
        error
      );

      alert.error(
        "Unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // CANCEL RELEASING
  // =========================
  const handleCancelReleasing = async (
    col
  ) => {
    const confirm = window.confirm(
      "Are you sure you want to cancel this releasing?"
    );

    if (!confirm) {
      return;
    }

    setLoading(true);

    try {
      const result = await ReleaseCancel(
        col.id
      );

      console.log(
        "ReleaseCancel:",
        result
      );

      if (
        !result ||
        result.S === false
      ) {
        alert.error(
          (result &&
            (result.error ||
              result.msg)) ||
            "Failed to cancel releasing"
        );

        return;
      }

      alert.success(
        result.msg ||
          result ||
          "Releasing cancelled successfully."
      );

      await getReleasing(
        page + 1,
        rowsPerPage,
        searchTerm
      );
    } catch (error) {
      console.error(
        "Cancel error:",
        error
      );

      alert.error(
        "Unexpected error during cancel."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // VIEW RELEASING
  // NEW TAB
  // =========================
  const handleGenerate = async (
    row
  ) => {
    // =========================
    // OPEN NEW TAB
    // =========================
    const printWindow = window.open(
      "",
      "_blank",
      "width=1100,height=900"
    );

    if (!printWindow) {
      alert.error(
        "Please allow pop-ups for this page."
      );
      return;
    }

    // =========================
    // TEMPORARY LOADING PAGE
    // =========================
    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Loading Releasing Form...</title>
        </head>

        <body
          style="
            margin:0;
            display:flex;
            align-items:center;
            justify-content:center;
            height:100vh;
            font-family:Arial,sans-serif;
            background:#f5f5f5;
            color:#555;
          "
        >
          Loading Releasing Form...
        </body>
      </html>
    `);

    printWindow.document.close();

    setLoading(true);

    try {
      // =========================
      // GET LOT DETAILS
      // =========================
      const results =
        await findAuctionLot(
          row.auction_id,
          row.lot_number
        );

      if (
        !results ||
        !Array.isArray(results.data)
      ) {
        printWindow.close();

        alert.error(
          "Failed to load releasing details."
        );
        return;
      }

      // =========================
      // SORT LOTS
      // =========================
      const lots = [
        ...results.data,
      ].sort(
        (a, b) =>
          Number(a.lot_number || 0) -
          Number(b.lot_number || 0)
      );

      // =========================
      // CURRENT DATE / TIME
      // =========================
      const now = new Date();

      const dateTime =
        now.toLocaleString(
          "en-PH",
          {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          }
        );

      // =========================
      // STAFF
      // =========================
      const staffName =
        row.staff_name ||
        `${Api.request.username || ""} ${
          Api.request.groupName || ""
        }`.trim();

      // =========================
      // ESCAPE HTML
      // =========================
      const escapeReportHtml = (
        value
      ) => {
        if (
          value === null ||
          value === undefined
        ) {
          return "";
        }

        return String(value)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      };
const formatHtmlText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .replace(/<br\s*\/?>/gi, "<br>")
    .replace(/&nbsp;/gi, " ");
};
      // =========================
      // REPORT ITEMS
      // 5 LOTS PER PRINTED PAGE
      // =========================
      const buildItemRow = (lot) => `
        <tr class="item-row">
          <td class="item-cell lot-cell">
            Lot #${escapeReportHtml(
              lot.lot_number || "---"
            )}
          </td>

          <td class="item-cell artist-cell">
            ${formatHtmlText(
              lot.artist || "---"
            )}
          </td>

          <td class="item-cell title-cell">
            ${formatHtmlText(
              lot.title || "---"
            )}
          </td>

          <td class="item-cell image-cell">
            ${
              lot.img_url
                ? `
                    <img
                      src="${escapeReportHtml(
                        lot.img_url
                      )}"
                      class="lot-image"
                      alt=""
                      onerror="
                        this.style.display='none';
                        this.nextElementSibling.style.display='flex';
                      "
                    />

                    <div
                      class="no-image"
                      style="display:none;"
                    >
                      No Image
                    </div>
                  `
                : `
                    <div class="no-image">
                      No Image
                    </div>
                  `
            }
          </td>
        </tr>
      `;

      const chunks = [];
      for (let i = 0; i < lots.length; i += 5) {
        chunks.push(lots.slice(i, i + 5));
      }

      if (!chunks.length) {
        chunks.push([]);
      }

      const buildPage = (pageLots, isLastPage) => {
        const rows = pageLots.length
          ? pageLots.map(buildItemRow).join("")
          : `
              <tr>
                <td
                  colspan="4"
                  class="empty-items"
                >
                  No released items found.
                </td>
              </tr>
            `;

        return `
          <div class="report-page">
            <table class="report-table">
              <colgroup>
                <col class="col-lot" />
                <col class="col-artist" />
                <col class="col-title" />
                <col class="col-image" />
              </colgroup>

              <thead>
                <tr>
                  <th colspan="4" class="report-header-cell report-header-info">
                    <div class="header">
                      <img
                        src="/static/logo140.png"
                        class="logo"
                        alt="Leon Gallery"
                        onerror="this.style.display='none';"
                      />
                    </div>

                    <div class="document-title">
                      Releasing Form
                    </div>

                    <div class="info">
                      <div class="info-top">
                        <div class="info-row">
                          <div class="info-label">Name</div>
                          <div class="info-value">
                            : ${escapeReportHtml(
                              row.client_name || "---"
                            )}
                          </div>
                        </div>

                        <div class="info-row date">
                          <div class="info-label"></div>
                          <div class="info-value">
                            Date: ${escapeReportHtml(dateTime)}
                          </div>
                        </div>
                      </div>

                      <div class="info-row purpose">
                        <div class="info-label">Purpose</div>
                        <div class="info-value">
                          : ${escapeReportHtml(
                            row.auction_name || "---"
                          )}
                        </div>
                      </div>
                    </div>
                  </th>
                </tr>

                <tr class="items-header-row">
                  <th class="items-header-cell">Lot</th>
                  <th class="items-header-cell">Artist</th>
                  <th class="items-header-cell">Title</th>
                  <th class="items-header-cell image-header-cell">Image</th>
                </tr>
              </thead>

              <tbody>
                ${rows}
              </tbody>
            </table>

            ${
              isLastPage
                ? `
                    <div class="signatures">
                      <div class="signature">
                        <div class="signature-line"></div>
                        <div class="signature-label">
                          Release To
                        </div>
                      </div>

                      <div class="signature">
                        <div class="signature-line"></div>
                        <div class="signature-label">
                          Release By
                        </div>
                      </div>
                    </div>
                  `
                : ""
            }
          </div>
        `;
      };

      const reportPages = chunks
        .map((pageLots, index) =>
          buildPage(
            pageLots,
            index === chunks.length - 1
          )
        )
        .join("");

// =========================
      // WRITE DOCUMENT
      // =========================
      printWindow.document.open();

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8" />

            <title>
              Releasing Form
            </title>

            <style>
              /* =====================
                 PAGE
              ===================== */

              @page {
                size: A4 portrait;
                margin: 15mm;
              }

              * {
                box-sizing: border-box;
              }

              html,
              body {
                margin: 0;
                padding: 0;
                background: #ffffff;
                color: #000000;
              }

              body {
                font-family:
                  Arial,
                  Helvetica,
                  sans-serif;
                font-size: 12px;
                line-height: 1.4;
              }

              /* =====================
                 PRINT BAR
              ===================== */

              .topbar {
                position: sticky;
                top: 0;
                z-index: 1000;
                min-height: 58px;
                padding: 10px 20px;
                background: #ffffff;
                border-bottom: 1px solid #ddd;
                box-shadow:
                  0 2px 8px rgba(0,0,0,0.08);
                display: flex;
                align-items: center;
                justify-content: flex-end;
              }

              .print-button {
                border: 0;
                background: #1976d2;
                color: #ffffff;
                padding: 9px 18px;
                border-radius: 6px;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
              }

              /* =====================
                 PAGE WRAPPER
              ===================== */

              .page {
                width: 100%;
                max-width: 180mm;
                margin: 25px auto;
                background: #ffffff;
                padding: 0;
              }

              /* =====================
                 REPEATING REPORT TABLE
              ===================== */

              .report-page {
                width: 100%;
                max-width: 180mm;
                margin: 25px auto 0;
                page-break-after: always;
                break-after: page;
              }

              .report-page:last-child {
                page-break-after: auto;
                break-after: auto;
              }

              .report-table {
                width: 100%;
                border-collapse: collapse;
                border-spacing: 0;
                table-layout: fixed;
              }

              .report-table thead {
                display: table-header-group;
              }

              .report-table tbody {
                display: table-row-group;
              }

              .report-header-cell,
              .report-body-cell,
              .report-footer-cell {
                padding: 0;
                border: 0;
              }

              .report-header-info {
                text-align: left;
                vertical-align: top;
              }

              /* =====================
                 HEADER
                 THIS REPEATS EVERY PRINTED PAGE
              ===================== */

              .header {
                text-align: center;
                padding: 0;
              }

              .logo {
                width: 180px;
                height: auto;
                display: block;
                margin: 0 auto 1px;
              }

              .document-title {
                text-align: center;
                font-size: 12px;
                font-weight: 700;
                margin: 0 0 8px;
              }

              /* =====================
                 INFORMATION
              ===================== */

              .info {
                width: 100%;
                padding-bottom: 9px;
                border-bottom: 1px solid #777;
                margin-bottom: 14px;
              }

              .info-top {
                display: grid;
                grid-template-columns: 1fr 1fr;
                width: 100%;
              }

              .info-row {
                display: flex;
                min-height: 17px;
                font-size: 11px;
              }

              .info-label {
                min-width: 50px;
              }

              .info-value {
                flex: 1;
              }

              .date {
                justify-content: flex-end;
                text-align: right;
              }

              .purpose {
                width: 100%;
                display: flex;
              }

              /* =====================
                 ITEM HEADER
              ===================== */

              .report-table .col-lot {
                width: 75px;
              }

              .report-table .col-artist {
                width: 32%;
              }

              .report-table .col-title {
                width: auto;
              }

              .report-table .col-image {
                width: 145px;
              }

              .report-header-top td {
                padding: 0;
                border: 0;
              }

              .items-header-row {
                background: #f7f7f7;
              }

              .items-header-cell {
                padding: 8px;
                border: 1px solid #cfcfcf;
                font-size: 10.5px;
                line-height: 1.2;
                font-weight: 700;
                text-align: left;
                vertical-align: middle;
                text-transform: none;
              }

              .items-header-cell:first-child {
                border-left: 1px solid #cfcfcf;
              }

              .image-header-cell {
                text-align: center;
              }

              /* =====================
                 ITEMS
              ===================== */

              .item-row {
                page-break-inside: avoid;
                break-inside: avoid;
              }

              .item-cell {
                padding: 8px;
                border-right: 1px solid #cfcfcf;
                border-bottom: 1px solid #cfcfcf;
                font-size: 10.5px;
                vertical-align: middle;
              }

              .item-cell:first-child {
                border-left: 1px solid #cfcfcf;
              }

              .lot-cell {
                width: 75px;
                text-align: center;
                font-weight: 700;
                white-space: nowrap;
              }

              .artist-cell {
                width: auto;
                line-height: 1.35;
                 word-break: break-word;
              }

              .title-cell {
                width: auto;
                line-height: 1.35;
                 word-break: break-word;
              }

              .image-cell {
                width: 145px;
                text-align: center;
              }

              .lot-image {
                width: 115px;
                height: 125px;
                object-fit: contain;
                display: block;
                margin: 0 auto;
              }

              .no-image {
                width: 115px;
                height: 125px;
                display: flex;
                align-items: center;
                justify-content: center;
                margin: 0 auto;
                background: #f3f4f6;
                border: 1px solid #d1d5db;
                color: #777;
                font-size: 9px;
              }

              .empty-items {
                padding: 40px 10px;
                text-align: center;
                color: #777;
                border: 1px solid #cfcfcf;
              }

              /* =====================
                 SIGNATURES
              ===================== */

              .signatures {
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: flex-start;
                gap: 120px;
                margin-top: 55px;
                page-break-inside: avoid;
                break-inside: avoid;
              }

              .signature {
                text-align: center;
              }

              .signature-line {
                width: 170px;
                max-width: 100%;
                margin: 0 auto;
                border-top: 1px solid #000;
              }

              .signature-label {
                margin-top: 5px;
                font-size: 11px;
              }

              /* =====================
                 PRINT
              ===================== */

              @media print {

                html,
                body {
                  background: #ffffff;
                }

                .topbar {
                  display: none !important;
                }

                .page {
                  width: 100%;
                  max-width: none;
                  margin: 0;
                  padding: 0;
                }

                .report-page {
                  width: 100%;
                  max-width: none;
                  margin: 0;
                  page-break-after: always;
                  break-after: page;
                }

                .report-page:last-child {
                  page-break-after: auto;
                  break-after: auto;
                }

                .report-table {
                  width: 100%;
                  border-collapse: collapse;
                }

                .report-table thead {
                  display: table-header-group;
                }

                .report-table tbody {
                  display: table-row-group;
                }

                .item-row,
                .item-cell,
                .signatures,
                img {
                  page-break-inside: avoid;
                  break-inside: avoid;
                }
              }
            </style>
          </head>

          <body>

            <!-- =====================
                 PRINT BUTTON
            ===================== -->

            <div class="topbar">
              <button
                class="print-button"
                onclick="window.print()"
              >
                🖨 Print
              </button>
            </div>


            <!-- =====================
                 REPORT
            ===================== -->

            <div class="page">
              ${reportPages}
            </div>

          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

    } catch (error) {

      console.error(
        "Failed to load releasing preview:",
        error
      );

      try {
        printWindow.close();
      } catch (e) {
        console.error(e);
      }

      alert.error(
        "Unable to load releasing preview."
      );

    } finally {
      setLoading(false);
    }
  };

  // =========================
  // RETURN
  // =========================
  return (
    <Box className={classes.root}>

      {/* =========================
          ADD RELEASING
      ========================= */}
      <Paper
        className={classes.addCard}
        elevation={0}
      >

        <Box
          className={
            classes.sectionHeader
          }
        >

          <Box
            display="flex"
            alignItems="center"
          >

            <EventNoteIcon
              color="primary"
              style={{
                marginRight: 10,
              }}
            />

            <Box>

              <Typography
                variant="h6"
                className={
                  classes.sectionTitle
                }
              >
                Release Item
              </Typography>

              <Typography
                variant="body2"
                className={
                  classes.sectionSubtitle
                }
              >
                Create a new item releasing
                record
              </Typography>

            </Box>

          </Box>

        </Box>

        <Divider />

        <Box
          className={
            classes.addContent
          }
        >

          <AddReleasingDialog
            onSubmit={
              handleAddSubmit
            }
          />

        </Box>

      </Paper>


      {/* =========================
          RELEASING RECORDS
      ========================= */}
      <Paper
        className={classes.card}
        elevation={0}
      >

        {/* =========================
            SEARCH
        ========================= */}
        <Box
          className={
            classes.searchSection
          }
        >

          <Typography
            variant="subtitle1"
            style={{
              fontWeight: 600,
            }}
          >
            Releasing Records
          </Typography>


          <Box
            className={
              classes.searchGroup
            }
          >

            <Box
              className={
                classes.searchBox
              }
            >

              <SearchIcon
                fontSize="small"
                style={{
                  color:
                    "#9ca3af",
                }}
              />

              <InputBase
                className={
                  classes.searchInput
                }
                placeholder="Search releasing records..."
                value={
                  searchTerm
                }
                onChange={(e) =>
                  setSearchTerm(
                    e.target.value
                  )
                }
                onKeyPress={(e) => {
                  if (
                    e.key ===
                    "Enter"
                  ) {
                    handleSearch();
                  }
                }}
              />


              {searchTerm && (
                <IconButton
                  size="small"
                  className={
                    classes.clearButton
                  }
                  onClick={
                    clearSearch
                  }
                >
                  <ClearIcon
                    fontSize="small"
                  />
                </IconButton>
              )}

            </Box>


            <Button
              variant="contained"
              color="primary"
              className={
                classes.searchButton
              }
              startIcon={
                <SearchIcon />
              }
              onClick={
                handleSearch
              }
            >
              Search
            </Button>

          </Box>

        </Box>


        <Divider />


        {/* =========================
            TABLE
        ========================= */}
        <TableContainer
          className={
            classes.tableContainer
          }
        >

          <Table
            className={
              classes.table
            }
          >

            <TableHead
              className={
                classes.tableHead
              }
            >

              <TableRow>

                <TableCell
                  className={
                    classes.tableHeader
                  }
                >
                  #
                </TableCell>


                <TableCell
                  className={
                    classes.tableHeader
                  }
                >
                  Auction Name
                </TableCell>


                <TableCell
                  className={
                    classes.tableHeader
                  }
                >
                  Client Name
                </TableCell>


                <TableCell
                  className={
                    classes.tableHeader
                  }
                >
                  Release Status
                </TableCell>


                <TableCell
                  className={
                    classes.tableHeader
                  }
                >
                  Staff Name
                </TableCell>


                <TableCell
                  className={
                    classes.tableHeader
                  }
                >
                  Created Date
                </TableCell>


                <TableCell
                  align="center"
                  className={
                    classes.tableHeader
                  }
                >
                  Action
                </TableCell>

              </TableRow>

            </TableHead>


            <TableBody>

              {data.length > 0 ? (

                data.map(
                  (row, index) => (

                    <TableRow
                      key={
                        row.id ||
                        index
                      }
                      className={
                        classes.tableRow
                      }
                    >

                      {/* NUMBER */}

                      <TableCell
                        className={
                          classes.tableCell
                        }
                      >
                        {
                          page *
                            rowsPerPage +
                            index +
                            1
                        }
                      </TableCell>


                      {/* AUCTION */}

                      <TableCell
                        className={
                          classes.tableCell
                        }
                      >

                        <Box
                          display="flex"
                          alignItems="center"
                        >

                          <EventNoteIcon
                            fontSize="small"
                            style={{
                              marginRight:
                                8,
                              color:
                                "#9ca3af",
                            }}
                          />

                          <Typography
                            variant="body2"
                            style={{
                              fontWeight:
                                500,
                            }}
                          >
                            {
                              row.auction_name ||
                              "-"
                            }
                          </Typography>

                        </Box>

                      </TableCell>


                      {/* CLIENT */}

                      <TableCell
                        className={
                          classes.tableCell
                        }
                      >

                        <Box
                          display="flex"
                          alignItems="center"
                        >

                          <PersonIcon
                            fontSize="small"
                            style={{
                              marginRight:
                                8,
                              color:
                                "#9ca3af",
                            }}
                          />

                          {
                            row.client_name ||
                            "-"
                          }

                        </Box>

                      </TableCell>


                      {/* STATUS */}

                      <TableCell
                        className={
                          classes.tableCell
                        }
                      >

                        <span
                          className={
                            classes.status
                          }
                        >
                          {
                            row.release_status ||
                            "-"
                          }
                        </span>

                      </TableCell>


                      {/* STAFF */}

                      <TableCell
                        className={
                          classes.tableCell
                        }
                      >
                        {
                          row.staff_name ||
                          "-"
                        }
                      </TableCell>


                      {/* DATE */}

                      <TableCell
                        className={
                          classes.tableCell
                        }
                      >

                        <Box
                          display="flex"
                          alignItems="center"
                        >

                          <AccessTimeIcon
                            fontSize="small"
                            style={{
                              marginRight:
                                6,
                              color:
                                "#9ca3af",
                            }}
                          />

                          {
                            formatDateTime(
                              row.created_date
                            )
                          }

                        </Box>

                      </TableCell>


                      {/* ACTION */}

                      <TableCell
                        align="center"
                        className={
                          classes.tableCell
                        }
                      >

                        <Box
                          className={
                            classes.actionWrapper
                          }
                        >

                          {/* VIEW */}

                          <Tooltip
                            title="View Releasing"
                          >

                            <IconButton
                              size="small"
                              className={
                                classes.actionButton
                              }
                              onClick={() =>
                                handleGenerate(
                                  row
                                )
                              }
                            >

                              <VisibilityIcon
                                fontSize="small"
                                color="primary"
                              />

                            </IconButton>

                          </Tooltip>


                          {/* CANCEL */}

                          <Tooltip
                            title="Cancel Releasing"
                          >

                            <IconButton
                              size="small"
                              className={
                                classes.actionButton
                              }
                              onClick={() =>
                                handleCancelReleasing(
                                  row
                                )
                              }
                            >

                              <CancelIcon
                                fontSize="small"
                                style={{
                                  color:
                                    "#d32f2f",
                                }}
                              />

                            </IconButton>

                          </Tooltip>

                        </Box>

                      </TableCell>

                    </TableRow>

                  )
                )

              ) : (

                <TableRow>

                  <TableCell
                    colSpan={7}
                    className={
                      classes.emptyState
                    }
                  >

                    <Typography
                      variant="body2"
                      color="textSecondary"
                    >
                      No releasing records
                      found
                    </Typography>

                  </TableCell>

                </TableRow>

              )}

            </TableBody>

          </Table>

        </TableContainer>


        {/* =========================
            PAGINATION
        ========================= */}

        <TablePagination
          className={
            classes.pagination
          }
          component="div"
          count={
            totalCount
          }
          page={
            Number(page)
          }
          onPageChange={
            handleChangePage
          }
          rowsPerPage={
            rowsPerPage
          }
          onChangeRowsPerPage={
            handleChangeRowsPerPage
          }
          rowsPerPageOptions={[
            10,
          ]}
        />

      </Paper>


      {/* =========================
          LOADING
      ========================= */}

      <Backdrop
        open={loading}
        className={
          classes.backdrop
        }
      >

        <CircularProgress
          color="inherit"
        />

      </Backdrop>

    </Box>
  );
};

export default PaginatedTableV4;